import React, { useState } from 'react';
import {
  useAlertCategories,
  useGenerateDefaultCatetories,
  useSetDefaultTeamForCategories,
} from './hooks/useAlertCategories';
import { StylishNewButton } from '../../components/DesignSystems/New/StylishNewButton';
import { Alert, Badge, Spinner } from 'react-bootstrap';
import LegacyPratusTable from '../../components/DesignSystems/PratusTable/LegacyPratusTable';
import { useDistributionLists } from '../../hooks/useDistributionLists';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from '../../slices/commonSelectors';
import { Link } from 'react-router';
import { SeverityLabel, TableColumn } from './types';
import { DGPTAlertCategory } from '../../types/dgptAlerts';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import AlertCategoryEditDrawer from './AlertCategoryEditDrawer';
import { MdEdit } from 'react-icons/md';

const SEVERITY_LABELS: Record<number, SeverityLabel> = {
  1: { label: '1 (Lowest)', variant: 'secondary' },
  2: { label: '2 (Low)', variant: 'info' },
  3: { label: '3 (Medium)', variant: 'warning' },
  4: { label: '4 (High)', variant: 'danger' },
  5: { label: '5 (Highest)', variant: 'danger' },
};

const AlertCategoryTable: React.FC = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const { data: categories, isLoading: isLoadingCategories } =
    useAlertCategories();
  const { data: teams = [], isLoading: isLoadingTeams } =
    useDistributionLists(groupGuid);
  const mutateGenerateDefaultCategories = useGenerateDefaultCatetories();
  const mutateSetDefaultTeam = useSetDefaultTeamForCategories();

  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(
    undefined
  );
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<DGPTAlertCategory | null>(null);

  const handleEditCategory = (category: DGPTAlertCategory) => {
    setSelectedCategory(category);
    setEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
    setSelectedCategory(null);
  };

  if (isLoadingCategories || isLoadingTeams) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const categoryCount = categories?.length || 0;
  const hasCategories = categoryCount > 0;
  const hasTeams = teams?.length > 0;

  const hasCategoriesWithoutTeams =
    categories?.some(
      (category: DGPTAlertCategory) =>
        !category.teams || category.teams.length === 0
    ) || false;

  const columns: TableColumn[] = [
    {
      dataField: 'severity',
      text: 'Severity',
      sort: true,
      formatter: (cell: number) => {
        const severityInfo = SEVERITY_LABELS[cell] || SEVERITY_LABELS[1];
        return <Badge bg={severityInfo.variant}>{severityInfo.label}</Badge>;
      },
    },
    {
      dataField: 'contactMethods',
      text: 'Contact Methods',
      sort: true,
      formatter: (cell: string[]) => (
        <small className="">{cell?.length ? cell.join(', ') : 'None'}</small>
      ),
    },
    {
      dataField: 'teams',
      text: 'Teams',
      sort: false,
      formatter: (_: string[], row: DGPTAlertCategory) => (
        <small className="">
          {row.teamNames?.length ? row.teamNames.join(', ') : 'None'}
        </small>
      ),
    },
    {
      dataField: 'ack',
      text: 'Acknowledgement Required',
      sort: true,
      formatter: (cell: boolean) => {
        return cell ? 'Yes' : 'No';
      },
    },
    {
      dataField: 'actions',
      text: 'Edit',
      sort: false,
      isDummyField: true,
      formatter: (_: any, row: DGPTAlertCategory) => (
        <StylishNewButton
          className="button--icon sml"
          onClick={() => handleEditCategory(row)}
        >
          <MdEdit />
        </StylishNewButton>
      ),
    },
  ];

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0">Alert Categories</h4>
        </div>

        <Alert variant="info" className="mb-3">
          <p className="mb-0">
            Acknowledgement is only required for PRATUS users. Non-users cannot be required to acknowledge Alerts.
          </p>
        </Alert>

        {!hasTeams && (
          <Alert variant="warning" className="mb-3">
            <strong>At least one Team is required for Alert Categories</strong>
            <p className="mb-0">
              You need to create at least one{' '}
              <Link to="/groups/teams">Team</Link> before you can properly set
              up alert categories.
            </p>
          </Alert>
        )}

        {hasCategoriesWithoutTeams && hasTeams && (
          <Alert variant="warning" className="mb-3">
            <strong>Categories Without Teams</strong>
            <p>
              Some alert categories do not have teams assigned. Please select a
              default team to assign to these categories.
            </p>
            <div className="d-flex mt-3">
              <div className="col-6">
                <StylishNewSelect
                  classes="light"
                  options={teams.map((team) => ({
                    label: team.name,
                    value: team.id,
                  }))}
                  onChange={(value) => setSelectedTeam(value.value)}
                />
              </div>
              <div className="col-6 justify-content-end d-flex align-items-center">
                <StylishNewButton
                  className="button--primary inverted"
                  disabled={!selectedTeam}
                  onClick={() =>
                    selectedTeam && mutateSetDefaultTeam.mutate(selectedTeam)
                  }
                >
                  Set Default Team
                </StylishNewButton>
              </div>
            </div>
          </Alert>
        )}

        {!hasCategories && hasTeams && (
          <div>
            <Alert variant="warning" className="mb-3">
              <strong>Alert Categories Required</strong>
              <p>
                You need to create alert categories before you can set up alert
                configurations. Alert categories define the severity of alerts
                and who receives them.
              </p>
              <p className="mb-0">
                Pick a Team and click the &quot;Generate Default
                Categories&quot; button to get started.
              </p>
              <div className=" d-flex mt-3">
                <div className="col-6">
                  <StylishNewSelect
                    classes="light"
                    options={teams.map((team) => ({
                      label: team.name,
                      value: team.id,
                    }))}
                    onChange={(value) => setSelectedTeam(value.value)}
                  />
                </div>
                <div
                  className={
                    'col-6 justify-content-end d-flex align-items-center'
                  }
                >
                  <StylishNewButton
                    className="button--primary inverted"
                    disabled={!selectedTeam}
                    onClick={() =>
                      mutateGenerateDefaultCategories.mutate(selectedTeam)
                    }
                  >
                    Generate Default Categories
                  </StylishNewButton>
                </div>
              </div>
            </Alert>
          </div>
        )}

        <div className="form-block">
          <LegacyPratusTable
            keyField="id"
            columns={columns}
            rows={categories || []}
            noDataIndication="No alert categories found"
          />
        </div>
      </div>

      {editDrawerOpen && (
        <AlertCategoryEditDrawer
          onClose={handleCloseEditDrawer}
          category={selectedCategory}
        />
      )}
    </>
  );
};

export default AlertCategoryTable;
